import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { cn } from '@/utils/utils.ts';
import style from '@/pages/dashboard/advanced/index.module.scss';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select.tsx';
import { SelectValue } from '@radix-ui/react-select';
import { DateRangePicker } from '@/components/ui/date-range-picker.tsx';
import type { RangeGranularity } from '@/utils/granularity.ts';
import { useOverviewQuery } from '@/features/dashboard/hooks/queries/get-overview.ts';
import { EnergySummaryCards } from '@/features/dashboard/components/admin/energy-summary-cards.tsx';
import { DashboardEnergyManagementChart } from '@/features/dashboard/components/admin/dashboard-energy-management-chart.tsx';
import { useMemo } from 'react';
import { usePerformanceMonitoringParams } from '../../hooks/use-performance-monitoring-params';
import PerformanceMonitoringOptions from '@/features/dashboard/components/admin/performance-monitoring-options.tsx';
import { useTranslation } from 'react-i18next';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { LastUpdateTimestamp } from '@/components/last-update-timestamp.tsx';

export const PerformanceMonitoring = ({ className }: { className: string }) => {
  const { t } = useTranslation();
  const { currentViewContext, setCurrentViewContext, dateRange, setDateRange } =
    usePerformanceMonitoringParams();

  const { data: performanceData, dataUpdatedAt: performanceUpdatedAt } = useOverviewQuery({
    dateRange,
    viewContext: currentViewContext,
    section: 'overallPerformance',
    enabled: false,
  });

  const { dataUpdatedAt: energyMgmtUpdatedAt } = useOverviewQuery({
    dateRange,
    viewContext: currentViewContext,
    section: 'energyManagement',
    enabled: false,
  });

  const overviewDataUpdatedAt = Math.max(performanceUpdatedAt, energyMgmtUpdatedAt);

  const { data: plants } = useGetPlantsQuery();

  const availableOverview = useMemo(
    () =>
      [{ value: 'all', label: t('plants.overview.allPowerPlants') }].concat(
        plants?.length
          ? plants
              ?.filter((plant) => plant.basicDetails.status !== 'Archived')
              ?.map(({ plantId, basicDetails }) => ({
                value: plantId,
                label: basicDetails.name,
              }))
          : []
      ),
    [plants, t]
  );

  const getPlantReport = () => {
    if (currentViewContext === 'all') return;

    const plant = plants?.find((p) => p.plantId === currentViewContext);
    if (!plant) return;

    const startDate = format(dateRange.startDate, 'MMM d, yyyy');
    const endDate = format(dateRange?.endDate ?? dateRange.startDate, 'MMM d, yyyy');
    const reportDate = startDate === endDate ? startDate : `${startDate} - ${endDate}`;

    const Battery_Charge = performanceData?.overallPerformance.Battery_Charge ?? 0;

    // TODO: Add Available Cycles (cicli reali garantati pentru baterii)
    const storageCapacity = Number(plant.technicalSpecifications.storageCapacity);
    const Battery_Cycles =
      Battery_Charge && storageCapacity > 0
        ? (Battery_Charge / storageCapacity).toFixed(2)
        : 0;

    return {
      reportDate,
      plantName: plant.basicDetails.name,
      plantLocation: `${plant.siteInformation?.country?.label ?? 'Unknown'}, ${plant.siteInformation?.city?.label ?? 'Unknown'}`,
      energyData: {
        yield: performanceData?.overallPerformance.Yield ?? 0,
        consumed: performanceData?.overallPerformance.Consumed ?? 0,
        consumedFromGrid: performanceData?.overallPerformance.Consumed_from_Grid ?? 0,
        fedToGrid: performanceData?.overallPerformance.Fed_to_Grid ?? 0,
        batteryCharge: performanceData?.overallPerformance.Battery_Charge ?? 0,
        batteryDischarge: performanceData?.overallPerformance.Battery_Discharge ?? 0,
        batteryCycles: Number(Battery_Cycles),
      },
    };
  };

  return (
    <DashboardCard className={cn('pb-5', className)}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex w-full justify-between">
            <div className="flex flex-col">
              <h3 className="leading font-semibold">
                {t('dashboard.performance_monitoring.title')}
              </h3>
              <span className="text-muted-foreground h-4 text-xs">
                <LastUpdateTimestamp updatedAt={overviewDataUpdatedAt} />
              </span>
            </div>

            <PerformanceMonitoringOptions
              startDate={dateRange.startDate}
              currentViewContext={currentViewContext}
              getPlantReport={getPlantReport}
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="overflow-hidden p-0.5 text-nowrap sm:w-full sm:max-w-[200px]">
              <Select
                value={currentViewContext}
                onValueChange={(e) => {
                  setCurrentViewContext(e);
                }}
              >
                <SelectTrigger className="h-9 bg-white">
                  <SelectValue
                    placeholder={t('dashboard.performance_monitoring.select_plant')}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {availableOverview?.map((p) => {
                      return (
                        <SelectItem value={p.value} key={p.value}>
                          <div className="overflow-hidden text-ellipsis">{p.label}</div>
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div>
              <DateRangePicker
                onUpdate={(values) => {
                  setDateRange({
                    startDate: values.range.from,
                    endDate: values.range.to,
                    rangeGranularity: values.rangeGranularity as Exclude<
                      RangeGranularity,
                      '1min' | '15min'
                    >,
                  });
                }}
                initialDateFrom={dateRange.startDate}
                initialDateTo={dateRange.endDate}
                locale="ro-RO"
                defaultGranularity="5min"
                hideGranularities={['1min', '15min']}
                defaultMode="single"
                availableModes={['single', 'range']}
              />
            </div>
          </div>
        </div>
        <div className={cn(style.content_grid)}>
          <EnergySummaryCards
            dateRange={dateRange}
            viewContext={currentViewContext}
            className={style.energy_summary_cards}
          />
          <DashboardEnergyManagementChart
            dateRange={dateRange}
            viewContext={currentViewContext}
            className={style.energy_management}
          />
        </div>
      </div>
    </DashboardCard>
  );
};
